import { provider } from '@/provider.js'

const labeledRecipient = (recipient) => {
  const { displayName, email, type, userId, teamId } = recipient
  // Keep displayName as just the name, not formatted with email
  // Formatting will be done when displaying using EmailAddress class
  return {
    displayName,
    email,
    type,
    userId,
    teamId
  }
}

const reselect = (allParticipants, {from, toRecipients, ccRecipients, bccRecipients}, replyAll = false) => {
  // When replying to a message:
  // - If replyAll is false: only reply to the "from" address
  // - If replyAll is true: include all original recipients (to, cc, bcc)
  let currentRecipients = []
  let currentRecipientsCc = []
  let currentRecipientsBcc = []
  
  if (replyAll) {
    // Reply All: include sender and all original recipients
    if (from && from.type === "Custom") {
      // External sender: include the sender, exclude toRecipients (support email)
      currentRecipients = [from]
    } else {
      // Internal sender or other: include both sender and toRecipients
      currentRecipients = from ? [from, ...(toRecipients || [])] : [...(toRecipients || [])]
    }
    currentRecipientsCc = [...(ccRecipients || [])]
    currentRecipientsBcc = [...(bccRecipients || [])]
  } else {
    // Reply: only include the "from" address
    currentRecipients = from ? [from] : []
    currentRecipientsCc = []
    currentRecipientsBcc = []
  }

  return {currentRecipients, currentRecipientsCc, currentRecipientsBcc}
}

const emailAddresses = (currentCase, orderedConversations) => {
  const emailFieldNames = []
  if (currentCase.schema && currentCase.schema.fieldsets) {
    for (const fieldset of currentCase.schema.fieldsets) {
      for (const field of fieldset.fields) {
        if (field.type === "email_address") {
          emailFieldNames.push(field.name)
        }
      }
    }
  }
  const emailAddresses = []
  if (emailFieldNames && emailFieldNames.length > 0) {
    for (const emailFieldName of emailFieldNames) {
      const email = currentCase.properties[emailFieldName]
      if (email) {
        emailAddresses.push({ displayName: email, email, type: "Custom", id: 0 })
      }
    }
  }
  const customRecipients = []
  for (const conversation of orderedConversations) {
    for (const convMessage of conversation.messages) {
      if (convMessage.from && convMessage.from.type === "Custom") {
        customRecipients.push(convMessage.from)
      }
      for (const recipient of convMessage.recipients) {
        if (recipient.type === "Custom") {
          customRecipients.push(recipient)
        }
      }
    }
  }
  for (const customRecipient of customRecipients) {
    const existingEmailAddress = emailAddresses.find((ee) => ee.displayName === customRecipient.email)
    if (!existingEmailAddress) {
      emailAddresses.push({ displayName: customRecipient.email, email: customRecipient.email, type: "Custom", id: customRecipient.id })
    }
  }
  return emailAddresses
}

export default {
  toggleLoading({ commit }) {
    commit("toggleLoading")
  },
  setLoading({ commit }, value) {
    commit("setLoading", value)
  },
  async createBookmark({ commit }, newBookmarkData) {
    const bookmarks = (await provider.reloadBookmarks()) || []
    const duplicate = bookmarks.find(bookmark => bookmark.route === newBookmarkData.route)
    if (!duplicate) {
      const bookmark = await provider.createBookmark(newBookmarkData)
      if (!bookmark.error) {
        commit("bookmarkAdded", bookmark)
        commit("displayFlashMessage", { message: bookmark.message, severity: "success" })
        return true
      } else {
        commit("displayFlashMessage", { message: bookmark.message, severity: "error" })
        return false
      }
    } else {
      commit("displayFlashMessage", { message: "Failed to add " + newBookmarkData.linkText + ", page already bookmarked!", severity: "error" })
      return false
    }
  },
  async deleteBookmark({ commit }, bookmarkId) {
    const response = await provider.deleteBookmark(bookmarkId)
    if (!response.error) {
      await commit("bookmarkRemoved", bookmarkId)
      return true
    } else {
      commit("displayFlashMessage", { message: response, severity: "error" })
      return false
    }
  },
  switchMenu({ commit, state }, data) {
    if (data.opposite) {
      data.previousMenu = (state.menu === "config") ? undefined : state.menu
      if (state.menu === "main") {
        data.menu = "config"
      } else if (state.menu === "config") {
        if (state.previousMenu) {
          data.menu = state.previousMenu
          data.previousMenu = undefined
        } else {
          data.menu = "main"
        }
      } else if (state.menu === "case") {
        data.menu = "config"
      } else {
        data.menu = "main"
      }
    } else {
      data.previousMenu = undefined
    }
    data.sameContext = false
    if (data && data.from && data.to) {
      if (data.menu === "case" && Object.values(data.from.params).join(",") === Object.values(data.to.params).join(",")) {
        data.sameContext = true
      }
    }
    commit("activateMenu", data)
  },
  handleOverviewRoute(_, route) {
    switch (route) {
      case "/dashboard/overview":
        return "Dashboard Overview"
      case "/admin/case_types":
        return "Configuration Overview"
      case "/admin/sla":
        return "SLA Overview"
      case "/users":
        return "Users Overview"
      case "/teams":
        return "Teams Overview"
      default:
        return "Overview"
    }
  },
  recordLastCaseContainer({ commit, state, rootState }, path) {
    const isCaseScreen = !!['/conversations', '/documents', '/timeline', '/notes', '/tasks', '/detail'].find(subPath => {
      if (rootState && rootState.cbroute && rootState.cbroute.path) {
        return rootState.cbroute.path.endsWith(subPath)
      }
      return false
    })
    if (!isCaseScreen) {
      // If navigating from a bucket route, preserve sort parameters and page number
      let pathToStore = path
      if (rootState && rootState.cbroute && rootState.cbroute.path && rootState.cbroute.path.includes('/buckets/')) {
        const currentQuery = rootState.cbroute.query
        if (currentQuery && (currentQuery.sortField || currentQuery.sortDirection || currentQuery.page)) {
          const queryParams = new URLSearchParams()
          if (currentQuery.sortField) queryParams.set('sortField', currentQuery.sortField)
          if (currentQuery.sortDirection) queryParams.set('sortDirection', currentQuery.sortDirection)
          if (currentQuery.page) queryParams.set('page', currentQuery.page)
          const queryString = queryParams.toString()
          if (queryString) {
            pathToStore = `${path}?${queryString}`
          }
        }
      }
      commit("recordLastCaseContainer", { path: pathToStore, pageTitle: state.pageTitle })  
    }
  },
  setClipboardCommsMessage({ commit, rootState }, msg) {
    commit("setClipboardCommsMessage", msg)
    commit("setLastVisitedInbox", rootState.cbroute.params.saved_search_id)
  },
  popoutMessage({commit, rootState}, {messageId}) {
    commit("setLastVisitedInbox", rootState.cbroute.params.saved_search_id)
    for (const savedSearch of rootState.currentUser.account.savedSearches) {
      const foundMessage = savedSearch.contents.messages.find(m => m.id.toString() === messageId.toString())
      if (foundMessage) {
        commit("showMessageSidePanel", foundMessage)
        break
      }
    }
  },
  hideSidePanel({commit}) {
    commit("hideSidePanel")
  },
  hideDocumentSidePanel({commit}) {
    commit("hideDocumentSidePanel")
  },
  viewFileToSide({commit}, {field, fileAttachment}) {
    commit("viewFileToSide", {field, fileAttachment})
  },
  clearClipboard({ commit }) {
    commit("clearClipboard")
  },
  setPageTitle({ commit }, pageTitle) {
    document.title = pageTitle
    commit("setPageTitle", pageTitle)
  },
  updateMenuColor({ commit }, color) {
    commit("setMenuColor", color)
  },
  updateWhiteTheme({ commit }, theme) {
    commit("setWhiteTheme", theme)
  },

  replyToConversation({commit, rootState}, ctx) {

  },
  async updateRecipientsListUsingPrefix({commit, rootState}, searchPrefix) {
    const recipients = await provider.accountRecipients(searchPrefix)
    const emailAddrs = emailAddresses(rootState.cases.currentlySelected.case, rootState.cases.currentlySelected.case.conversations) 
    const participants = []
    let index = 0
    if (emailAddrs && emailAddrs.length > 0) {
      participants.push({ header: "Case Participants" })
      participants.push(...emailAddrs.map(recipient => Object.assign(labeledRecipient(recipient), { index: index++ })))
    }

    if (recipients && recipients.length > 0) {
      participants.push({ header: "All" })
      participants.push(...recipients.map(recipient => Object.assign(labeledRecipient(recipient), { index: index++ })))
    }
    commit("updateRecipientsList", participants)
  },
  async composeNewMessage({commit, rootState, dispatch}, ctx) {
    const recipients = await provider.accountRecipients()
    const participants = []
    let index = 0
    const emailAddrs = emailAddresses(rootState.cases.currentlySelected.case, rootState.cases.currentlySelected.case.conversations) 

    if (emailAddrs && emailAddrs.length > 0) {
      participants.push({ header: "Case Participants" })
      participants.push(...emailAddrs.map(recipient => Object.assign(labeledRecipient(recipient), { index: index++ })))
    }

    if (recipients && recipients.length > 0) {
      participants.push({ header: "All" })
      participants.push(...recipients.map(recipient => Object.assign(labeledRecipient(recipient), { index: index++ })))
    }
    ctx.allParticipants = participants    

    // Get all response templates from the current case type
    // Note: The current case type should already be up-to-date thanks to the createEmailTemplate mutation
    // which updates caseTypes.current when a new template is created
    let currentCaseType = rootState.caseTypes.current
    
    // If we have a currentCase with a caseTypeId, ensure we're using the right case type
    if (ctx.currentCase && ctx.currentCase.caseTypeId) {
      const caseTypeId = parseInt(ctx.currentCase.caseTypeId, 10)
      // Find the case type from all case types if current doesn't match
      if (!currentCaseType || parseInt(currentCaseType.id, 10) !== caseTypeId) {
        currentCaseType = rootState.caseTypes.all.find(ct => parseInt(ct.id, 10) === caseTypeId)
      }
    }
    
    // Ensure we have a valid case type and emailTemplates array
    // Handle cases where emailTemplates might be null, undefined, or not an array
    const emailTemplates = (currentCaseType && 
                            currentCaseType.emailTemplates && 
                            Array.isArray(currentCaseType.emailTemplates))
      ? currentCaseType.emailTemplates
      : []
    
    // Debug logging to help diagnose issues
    if (!currentCaseType) {
      console.warn('[composeNewMessage] No case type found', {
        currentCaseTypeId: rootState.caseTypes.current && rootState.caseTypes.current.id,
        requestedCaseTypeId: ctx.currentCase && ctx.currentCase.caseTypeId,
        allCaseTypeIds: rootState.caseTypes.all.map(ct => ct.id)
      })
    } else if (!emailTemplates || emailTemplates.length === 0) {
      console.warn('[composeNewMessage] No email templates found', {
        caseTypeId: currentCaseType.id,
        caseTypeName: currentCaseType.name,
        emailTemplatesExists: !!currentCaseType.emailTemplates,
        emailTemplatesType: typeof currentCaseType.emailTemplates,
        emailTemplatesIsArray: Array.isArray(currentCaseType.emailTemplates),
        emailTemplatesLength: currentCaseType.emailTemplates && currentCaseType.emailTemplates.length
      })
    }
    
    // Log each template's emailBodyTemplate value
    emailTemplates.forEach((t, idx) => {
      console.log(`[composeNewMessage] Template ${idx}:`, {
        id: t.id,
        name: t.name,
        emailBodyTemplate: t.emailBodyTemplate,
        emailBodyTemplateType: typeof t.emailBodyTemplate,
        emailBodyTemplateValue: JSON.stringify(t.emailBodyTemplate),
        passesFilter: t.emailBodyTemplate === true || t.emailBodyTemplate === "true" || t.emailBodyTemplate === 1
      })
    })
    
    // Filter for response templates - check for truthy emailBodyTemplate value
    // Try multiple ways to check since the value might be stored differently
    const allBodyTemplates = emailTemplates
      .filter(t => {
        const isBodyTemplate = t.emailBodyTemplate === true || 
                              t.emailBodyTemplate === "true" || 
                              t.emailBodyTemplate === 1 ||
                              t.emailBodyTemplate === "1" ||
                              (typeof t.emailBodyTemplate === "boolean" && t.emailBodyTemplate) ||
                              (typeof t.emailBodyTemplate === "string" && t.emailBodyTemplate.toLowerCase() === "true")
        console.log(`[composeNewMessage] Filter check for template ${t.id} (${t.name}): emailBodyTemplate=${t.emailBodyTemplate}, type=${typeof t.emailBodyTemplate}, passes=${isBodyTemplate}`)
        return isBodyTemplate
      })
      .map(t => ({id: t.id, name: t.name, defaultEmailBodyTemplate: t.defaultEmailBodyTemplate}))
    
    console.log('[composeNewMessage] allBodyTemplates:', allBodyTemplates)
    ctx.allBodyTemplates = allBodyTemplates

    if (ctx.conversationId) {
      const currentConverversation = ctx.currentCase.conversations.find(c => parseInt(c.id, 10) === ctx.conversationId)
      if (currentConverversation && currentConverversation.messages && currentConverversation.messages.length > 0) {
        const lastMessage = currentConverversation.messages[currentConverversation.messages.length - 1]
        const replyAll = ctx.replyAll !== undefined ? ctx.replyAll : false
        const {currentRecipients, currentRecipientsCc, currentRecipientsBcc} = reselect(participants, lastMessage, replyAll)  
        ctx.currentRecipients = currentRecipients 
        ctx.currentRecipientsCc = currentRecipientsCc 
        ctx.currentRecipientsBcc = currentRecipientsBcc 
      } else {
        ctx.currentRecipients = []
        ctx.currentRecipientsCc = []
        ctx.currentRecipientsBcc = []
      }
    } else {
      ctx.currentRecipients = []
      ctx.currentRecipientsCc = []
      ctx.currentRecipientsBcc = []
    }

    ctx.title = "New Message"
    
    commit("composeNewMessage", ctx)
  },
  dismissNewMessage({commit}) {
    commit("dismissNewMessage")
  },
  toggleMainMenuExpansion({commit}) {
    commit("toggleMainMenuExpansion")
    commit("hideSidePanel")
    commit("hideDocumentSidePanel")
  }
}
